import { useCollectionServices } from '../../contexts/CollectionSourceContext';
import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EventRepeatOutlined from '@mui/icons-material/EventRepeatOutlined';
import ViewListOutlined from '@mui/icons-material/ViewListOutlined';
import HelpOutline from '@mui/icons-material/HelpOutline';
import FitbitDailyExtractionSetup from './fitbit/FitbitDailyExtractionSetup';
import FitbitDailyExtractionManager from './fitbit/FitbitDailyExtractionManager';
import { DailyExtractionEdit } from '../../services/fitbitDailyExtractionService';

export function DailyExtractionSetupButton({ studyId }: { studyId: string }) {
  const [open, setOpen] = useState<'new' | 'edit' | 'view' | null>(null);
  const [editing, setEditing] = useState<DailyExtractionEdit | null>(null);
  const [confirmation, setConfirmation] = useState('');
  useEffect(() => { setOpen(null); setEditing(null); setConfirmation(''); }, [studyId]);
  return <><Stack direction={{ xs: 'column', md: 'row' }} gap={1} sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
    <Button variant="text" disabled={!studyId} startIcon={<ViewListOutlined />} onClick={() => { setConfirmation(''); setOpen('view'); }} sx={{ minHeight: 44, borderRadius: '12px' }}>View daily extractions</Button>
    <Tooltip title="Create a named setup with its own participants, measurements, schedule and optional email recipients."><Box component="span" sx={{ display: 'inline-flex' }}>
      <Button variant="outlined" disabled={!studyId} onClick={() => { setConfirmation(''); setOpen('new'); }} startIcon={<EventRepeatOutlined />}
        sx={{ minHeight: 44, width: '100%', borderRadius: '12px' }}>
        Set up daily extraction
      </Button>
    </Box></Tooltip>
  </Stack>{open === 'view' && <FitbitDailyExtractionManager key={studyId} studyId={studyId} onClose={() => setOpen(null)}
    onNew={() => setOpen('new')} onEdit={options => { setEditing(options); setOpen('edit'); }} />}
  {(open === 'new' || open === 'edit') && <FitbitDailyExtractionSetup key={`${studyId}-${open}-${editing?.scheduleId || ''}`} studyId={studyId}
    {...(open === 'edit' && editing ? editing : {})} createNew={open === 'new'} onClose={() => setOpen(null)} onSaved={configuration => {
    const nextRun = configuration.enabled && configuration.nextRunAt ? new Date(configuration.nextRunAt).toLocaleString([], {
      timeZone: configuration.timeZone, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    }) : null;
    setConfirmation(configuration.enabled ? `Daily extraction saved.${nextRun ? ` Next run: ${nextRun} (${configuration.timeZone}).` : ''}` : 'Daily extraction paused.');
  }} />}
    <Snackbar open={!!confirmation} autoHideDuration={7000} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={(_event, reason) => { if (reason !== 'clickaway') setConfirmation(''); }}>
      <Alert severity="success" variant="filled" onClose={() => setConfirmation('')} sx={{ width: '100%', borderRadius: '12px' }}>{confirmation}</Alert>
    </Snackbar>
  </>;
}

function FolderExample({ daily = false }: { daily?: boolean }) {
  const { source } = useCollectionServices();
  const rows = [
    { label: daily ? 'daily-exports' : source.exportRoot, detail: daily ? 'Daily files' : 'Manual extractions' },
    { label: 'Alex Morgan', detail: 'Researcher name' },
    { label: 'alextaylor1234', detail: 'Participant tracking ID' },
    { label: daily ? 'Daily extraction 09-17-26.json' : 'Heart rate 09-17-26.json', detail: daily ? 'Yesterday’s data · dated when created' : 'Extraction name + creation date (MM-DD-YY)', file: true },
  ];
  return <Box component="ol" aria-label={daily ? 'Daily export folders' : `${source.label} export folders`} sx={{ listStyle: 'none', m: 0, p: 0 }}>
    {rows.map((row, index) => <Box component="li" key={row.label} sx={{ ml: index * 1.5, pl: 1.5, py: 1,
      borderLeft: '1px solid #e7e8ec', minWidth: 0 }}>
      <Stack direction="row" gap={1} alignItems="flex-start">
        {row.file ? <DescriptionOutlined sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }} /> :
          <FolderOutlined sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />}
        <Box sx={{ minWidth: 0 }}><Typography sx={{ fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere' }}>{row.label}</Typography>
          <Typography variant="caption" color="text.secondary">{row.detail}</Typography></Box>
      </Stack>
    </Box>)}
  </Box>;
}

export default function StudyExportFolders() {
  const { source } = useCollectionServices();
  const [open, setOpen] = useState(false);
  return <>
    <Button variant="outlined" startIcon={<HelpOutline />} onClick={() => setOpen(true)}
      sx={{ minHeight: 44, borderRadius: '12px', width: { xs: '100%', sm: 'auto' } }}>
      How Google Cloud works
    </Button>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" aria-labelledby="export-folder-guide-title"
      PaperProps={{ sx: { borderRadius: '16px', m: { xs: 1, sm: 3 }, width: 'calc(100% - 16px)' } }}>
      <DialogTitle id="export-folder-guide-title">How Google Cloud works</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: '#fafafb' }}>
        <Typography variant="body2" color="text.secondary">A bucket is your study’s file storage in Google Cloud. Each study has its own bucket.</Typography>
        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>Create an extraction in StudySync. When it finishes, its file is saved in the bucket. Download it there or from Your extraction jobs.</Typography>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 650, mb: 1.5 }}>Inside your study’s bucket</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', minWidth: 0 }}>
            <Typography component="h3" sx={{ fontSize: 16, fontWeight: 650, mb: 1 }}>{source.label} exports</Typography>
            <FolderExample />
          </Paper>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', minWidth: 0 }}>
            <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
              <Typography component="h3" sx={{ fontSize: 16, fontWeight: 650 }}>Daily exports</Typography>
            </Stack>
            <FolderExample daily />
          </Paper>
        </Box>
        <Typography variant="body2" sx={{ mt: 2 }}>Daily exports contain yesterday’s data only when enabled in Set up daily extraction. Daily sync still rechecks the previous seven days for delayed uploads.</Typography>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>Example folders shown. A small suffix separates matching folder names or repeated extractions. Files sit directly inside the participant’s folder.</Typography>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>The bucket opens in a new tab. Sign in with the Google account that has access to your study’s bucket.</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}><Button variant="contained" onClick={() => setOpen(false)}>Done</Button></DialogActions>
    </Dialog>
  </>;
}
